<template>
    <DsModalDialog
        v-model="modelValue"
        :prevent-close="isCurrentlySpinning"
        theme="dark"
        class="bonusomat-spin-modal"
        :class="modClass"
    >
        <template #top> </template>
        <template #content>
            <div class="bonusomat-spin-modal__content">
                <div class="bonusomat-spin-modal__allwyn-joker-img-wrap">
                    <WebpImage
                        v-if="isWinningJoker"
                        src="/frontend/web/img/logos/allwyn/allwyn-joker-logo-turquoise.svg"
                        alt="allwyn Joker logo"
                    />
                </div>
                <BonusomatSpinWinner :state="state" :prize-text="winData?.winBoxTitle" />
                <BonusomatSpinAutomat
                    :state="state"
                    :win-icons="[winData?.reelIcon1, winData?.reelIcon2, winData?.reelIcon3]"
                    @spin-stopped="spinStoppedHandler"
                />
                <div v-if="isWinningWithAllwyn" class="bonusomat-spin-modal__allwyn">
                    <WebpImage
                        src="/frontend/web/img/logos/allwyn/allwyn-logo.png"
                        alt=""
                        class="bonusomat-spin-modal__allwyn-logo"
                    />
                    <div>Získali jste lístek do slosování o Allwyn zážitek!</div>
                </div>
                <BonusomatSpinButtons
                    :state="state"
                    :allwyn-page-url="isWinningWithAllwyn ? allwynInformationPageUrl : null"
                    @spin-click="startSpinHandler"
                    @close-modal="modelValue = false"
                />
                <BonusomatSpinFooter
                    :remaining-spins-count="remainingSpinsCount"
                    @show-winner-table="showWinnerTableHandler"
                />
            </div>
        </template>
        <template #bottom> </template>
    </DsModalDialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import WebpImage from "@sazka/design-system/src/image/webp-image.vue";
import DsModalDialog from "@sazka/design-system/src/modal-dialog/ds-modal-dialog.vue";
import { useNgSession } from "@sazka/ng/ng-session";
import { DateTimeUtils } from "@sazka/utils/date-time-utils";
import { sazkaAlert } from "@sazka/utils/sazka-alert";
import { useLoyaltyPlayerStatus } from "@/loyalty/composables/use-loyalty-player-status";
import BonusomatSpinAutomat from "./bonusomat-spin-modal/bonusomat-spin-automat.vue";
import BonusomatSpinButtons from "./bonusomat-spin-modal/bonusomat-spin-buttons.vue";
import BonusomatSpinFooter from "./bonusomat-spin-modal/bonusomat-spin-footer.vue";
import BonusomatSpinWinner from "./bonusomat-spin-modal/bonusomat-spin-winner.vue";
import { callBonusomatSpin } from "../../api/bonusomat-player-data-api";
import useBonusomatData from "../../composables/use-bonusomat-data";
import { BonusomatSlotsSpin } from "../../models/bonusomat-slots";
import { SpinAutomatState } from "../../models/bonusomat-spin-types";
import { BonusomatDatalayer } from "../../utils/analytics/bonusomat-datalayer";

const emit = defineEmits(["open-win-table"]);

const modelValue = defineModel<boolean>({ required: true });

const { session, refreshBalance } = useNgSession();

const { bonusomatPlayerStatus } = useLoyaltyPlayerStatus();
const { bonusomatPlayerData, remainingSpinsCount, loadBonusomatPlayerData } = bonusomatPlayerStatus;
const { allwynDrawData, loadAllwynDrawData } = useBonusomatData();

const state = ref<SpinAutomatState>("EMPTY");
const allwynInformationPageUrl = appSettings["AllwynExperiencePageUrl"];
const winData = ref<BonusomatSlotsSpin>(null);

const isCurrentlySpinning = computed(() => state.value == "ROLLING" || state.value == "STOPPING");
const isWinningWithAllwyn = computed(() => allwynDrawData.value && state.value == "WINNING");
const isWinningJoker = computed(() => state.value === "WINNING" && winData.value?.awardType?.toLowerCase() == "joker");

const modClass = computed(() => {
    return {
        lighten: state.value != "DISABLED" && state.value != "WINNING",
        winner: state.value === "WINNING" && winData.value?.winBoxTitle,
        joker: isWinningJoker.value,
        allwyn: isWinningWithAllwyn.value,
    };
});

const initModalState = () => {
    const isLastSpinToday = bonusomatPlayerData.value.lastSpin
        ? DateTimeUtils.isToday(new Date(bonusomatPlayerData.value.lastSpin))
        : false;
    if (isLastSpinToday && remainingSpinsCount.value > 0) {
        state.value = "DISABLED";
    } else if (remainingSpinsCount.value > 0) {
        state.value = "ACTIVE";
    } else {
        state.value = "EMPTY";
    }
};

const startSpinHandler = async () => {
    try {
        state.value = "ROLLING";
        const delay = new Promise((resolve) => setTimeout(resolve, 2800));

        winData.value = await callBonusomatSpin(session.value?.PlayerID);
        await delay;

        BonusomatDatalayer.pushSuccessfullSpin();

        state.value = "STOPPING";
    } catch (err: unknown) {
        sazkaAlert("Chyba", "Při pokusu točení Bonusomatem se nám něco nepovedlo. Zkuste to prosím znovu.");
        state.value = "ACTIVE";

        BonusomatDatalayer.pushSpinError(err as string);
    }
};

const spinStoppedHandler = async () => {
    state.value = "WINNING";
};

const showWinnerTableHandler = () => {
    emit("open-win-table");
};

const closeModalInternal = async () => {
    if (state.value == "WINNING") {
        await loadBonusomatPlayerData();
        await refreshBalance(true);

        if (allwynDrawData.value) {
            await loadAllwynDrawData();
        }
    }
};

watch(
    modelValue,
    (newModalIsOpen, oldModalIsOpen) => {
        if (newModalIsOpen) {
            initModalState();
        } else if (oldModalIsOpen) {
            closeModalInternal();
        }
    },
    {
        immediate: true,
    }
);
</script>
